import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import {
  ActivityStatus,
  ActivityType,
  ExecutionPlanType,
  WorkflowOverviewStatsQuery,
} from "generated-graphql/graphql";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { KeyMetric } from "./KeyMetric";
import { AssignmentManagementTableFilterKeys } from "../AssignmentManagerTable/useAssignmentManagerOmnisearch";

export function WorkflowOverviewKeyMetrics({
  data,
  loading,
}: {
  data?: WorkflowOverviewStatsQuery["workflowOverviewSeasonSummary"];
  loading: boolean;
}) {
  const theme = useTheme();
  const { createUrlFromOmnisearchKeyValues } = useOmnisearchDatagrid({
    persistenceKey: "workflow-overview-key-metrics",
  });

  // In Error metrics
  const inErrorHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  const inErrorUntaggedHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
      [AssignmentManagementTableFilterKeys.Tags, "null"],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  const inErrorComplianceHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
      [AssignmentManagementTableFilterKeys.Tags, "compliance"],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  const inErrorEngineeringHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.Error],
      [AssignmentManagementTableFilterKeys.Tags, "engineering"],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  // Wait for All
  const waitForAllHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [
        AssignmentManagementTableFilterKeys.ActivityType,
        `${ActivityType.WaitForAllStateReports}|${ActivityType.WaitForAllCredentialReports}|${ActivityType.WaitForAllCnReports}|${ActivityType.WaitForAllAltIdReports}|${ActivityType.WaitForAllLepc}|${ActivityType.WaitForAllFd}`,
      ],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  // In Manual metrics
  const inManualHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ExecutionPlan, ExecutionPlanType.Human],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  const inManualUnassignedHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ExecutionPlan, ExecutionPlanType.Human],
      [AssignmentManagementTableFilterKeys.Assignee, "Unassigned"],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  // In Progress
  const inProgressHref = createUrlFromOmnisearchKeyValues({
    keyValues: [
      [AssignmentManagementTableFilterKeys.ActivityStatus, ActivityStatus.InProgress],
    ],
    keysToKeep: ["reportingYear", "reportKind"],
  });

  return (
    <Paper
      sx={{
        height: "100%",
        width: "100%",
        py: theme.spacing(2),
        px: theme.spacing(3),
      }}
      elevation={0}
    >
      <Stack direction="column" spacing={theme.spacing(3)} height="100%">
        <Typography variant="body1" color={theme.palette.text.secondary}>
          Key Metrics
        </Typography>

        <Stack direction="column" spacing={theme.spacing(3)} flex={1}>
          {/* Error Metrics Row */}
          <Box>
            <Typography
              variant="caption"
              color={theme.palette.text.secondary}
              sx={{ mb: 1, display: "block", fontWeight: 500 }}
            >
              Errors
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: theme.spacing(2),
              }}
            >
              <KeyMetric
                name="Total"
                value={data?.workflowsCountError}
                loading={loading}
                href={inErrorHref}
              />
              <KeyMetric
                name="Untagged"
                value={data?.workflowsCountErrorUntagged}
                loading={loading}
                href={inErrorUntaggedHref}
              />
              <KeyMetric
                name="Compliance"
                value={data?.workflowsCountErrorCompliance}
                loading={loading}
                href={inErrorComplianceHref}
              />
              <KeyMetric
                name="Engineering"
                value={data?.workflowsCountErrorEngineering}
                loading={loading}
                href={inErrorEngineeringHref}
              />
            </Box>
          </Box>

          {/* Other Metrics Row */}
          <Box>
            <Typography
              variant="caption"
              color={theme.palette.text.secondary}
              sx={{ mb: 1, display: "block", fontWeight: 500 }}
            >
              Workflow Status
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: theme.spacing(2),
              }}
            >
              <KeyMetric
                name="Wait for All"
                value={data?.workflowsCountWaitForAll}
                loading={loading}
                href={waitForAllHref}
              />
              <KeyMetric
                name="In Manual"
                value={data?.workflowsCountHuman}
                loading={loading}
                href={inManualHref}
              />
              <KeyMetric
                name="Manual Unassigned"
                value={data?.workflowsCountHumanUnassigned}
                loading={loading}
                href={inManualUnassignedHref}
              />
              <KeyMetric
                name="In Progress"
                value={data?.workflowsCountInProgress}
                loading={loading}
                href={inProgressHref}
              />
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}
